import {
  AddProductToMyWishlistRequestedAction,
  AddProductToMyWishlistRequestedPayload,
  DeleteProductFromMyWishlistFailedAction,
  DeleteProductFromMyWishlistSucceededAction,
  addProductToMyWishlistFailed,
  addProductToMyWishlistSucceeded,
  deleteProductFromMyWishlistFailedType,
  deleteProductFromMyWishlistRequested,
  deleteProductFromMyWishlistSucceededType
} from '@spa/lib/store/wishlists/wishlistsSlice';
import {
  ApiAddProductToMyWishlistFailedAction,
  ApiAddProductToMyWishlistSucceededAction,
  apiAddProductToMyWishlistFailedType,
  apiAddProductToMyWishlistRequested,
  apiAddProductToMyWishlistSucceededType
} from '@spa/lib/store/api/wishlists/addProductToMyWishlistApi';
import { call, put, select } from 'typed-redux-saga';
import { SessionStateContent } from '@spa/lib/store/session/SessionState';
import { StateWishlist } from '@spa/lib/store/wishlists/WishlistsState';
import { convertAnalyticsProductToWishlistItem } from '@spa/lib/tracking/convertAnalyticsProductToWishlistItem';
import generateActionId from '@spa/lib/generateActionId';
import selectAuthenticatedUserId from '@spa/lib/store/auth/selectors/selectAuthenticatedUserId';
import selectCountryCode from '@spa/lib/store/settings/selectors/selectCountryCode';
import selectDefaultWishlist from '@spa/lib/store/wishlists/selectors/selectDefaultWishlist';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import selectPricesIncludeTax from '@spa/lib/store/settings/selectors/selectPricesIncludeTax';
import selectSessionContent from '@spa/lib/store/session/selectors/selectSessionContent';
import selectSessionUserId from '@spa/lib/store/session/selectors/selectSessionUserId';
import { setContent } from '@spa/lib/store/session/sessionSlice';
import { tracker } from '@spa/lib/tracking/tracker';
import waitForSucceededOrFailedAction from '@spa/lib/store/sagas/session/waitForSucceededOrFailedAction';

function* addProductToMyWishlist({
  actionId: requestedActionId,
  input,
  listAttribution,
  product
}: Pick<
  AddProductToMyWishlistRequestedPayload,
  'actionId' | 'input' | 'listAttribution' | 'product'
>): Generator {
  const actionId: number = generateActionId();
  yield* put(
    apiAddProductToMyWishlistRequested({ actionId, input, parentActionId: requestedActionId })
  );

  const waitResult = yield* waitForSucceededOrFailedAction<
    typeof apiAddProductToMyWishlistSucceededType,
    typeof apiAddProductToMyWishlistFailedType,
    ApiAddProductToMyWishlistSucceededAction,
    ApiAddProductToMyWishlistFailedAction
  >(actionId, apiAddProductToMyWishlistSucceededType, apiAddProductToMyWishlistFailedType);

  if (waitResult.type === 'succeeded') {
    const session: SessionStateContent = yield* select(selectSessionContent);
    const newSession: SessionStateContent = { ...session, selectedWishlist: input.wishlistId };
    yield* put(
      setContent({
        localChange: false,
        content: newSession
      })
    );
    yield* put(
      addProductToMyWishlistSucceeded({
        requestedActionId,
        wishlists: waitResult.action.payload.result
      })
    );

    const anonymousId: string = yield* select(selectSessionUserId);
    const authenticatedId: string | null = yield* select(selectAuthenticatedUserId);
    const country: string = yield* select(selectCountryCode);
    const { preOrderEnabled } = yield* select(selectGeneralSettings);
    const pricesIncludeTax: boolean = yield* select(selectPricesIncludeTax);
    const item = convertAnalyticsProductToWishlistItem(
      product,
      listAttribution,
      country,
      preOrderEnabled,
      pricesIncludeTax
    );

    yield* call(() =>
      tracker.addToWishlist({
        anonymousId,
        authenticatedId,
        ecommerce: {
          currency: product.currentPrice.currencyIso,
          value: item.price,
          ...(item.item_list_id ? { item_list_id: item.item_list_id } : {}),
          ...(item.item_list_name ? { item_list_name: item.item_list_name } : {}),
          items: [item]
        }
      })
    );
  } else {
    yield* put(
      addProductToMyWishlistFailed({
        error: waitResult.action.payload.error,
        requestedActionId
      })
    );
  }
}

function* addProductToMyWishlistSaga({
  payload: {
    actionId: requestedActionId,
    input,
    isDefault,
    listAttribution,
    product,
    deletedWishlist
  }
}: AddProductToMyWishlistRequestedAction): Generator {
  if (deletedWishlist !== undefined && isDefault !== undefined && product !== undefined) {
    const actionId: number = generateActionId();
    const defaultWishlist: StateWishlist | undefined = yield* select(selectDefaultWishlist);
    yield* put(
      deleteProductFromMyWishlistRequested({
        actionId,
        input: deletedWishlist,
        isDefault: defaultWishlist?.id === deletedWishlist.id,
        product
      })
    );

    const waitResult = yield* waitForSucceededOrFailedAction<
      typeof deleteProductFromMyWishlistSucceededType,
      typeof deleteProductFromMyWishlistFailedType,
      DeleteProductFromMyWishlistSucceededAction,
      DeleteProductFromMyWishlistFailedAction
    >(actionId, deleteProductFromMyWishlistSucceededType, deleteProductFromMyWishlistFailedType);

    if (waitResult.type === 'succeeded') {
      yield* addProductToMyWishlist({
        actionId: requestedActionId,
        input,
        listAttribution,
        product
      });
    } else {
      yield* put(
        addProductToMyWishlistFailed({
          error: waitResult.action.payload.error,
          requestedActionId
        })
      );
    }
  } else {
    yield* addProductToMyWishlist({
      actionId: requestedActionId,
      input,
      listAttribution,
      product
    });
  }
}

export default addProductToMyWishlistSaga;
